import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import {
  Bold,
  Italic,
  Heading,
  Quote,
  Code,
  Link as LinkIcon,
  List,
  ListOrdered,
  ListChecks,
  Paperclip,
  Undo2,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import { cn } from '@/utils/styleUtils';
import { useFileUpload } from '@/hooks/mutations/file-upload/useFileUpload';
import { getMediaUrl } from '@/utils/common';
import { useToast } from '@/components/ui/use-toast';
import {
  applyHeading,
  insertAtCursor,
  markdownForFile,
  parseUploadedUrl,
  prefixLines,
  wrapSelection,
  type TextareaSelection,
} from './markdownEditorUtils';

const HISTORY_LIMIT = 50;

const previewComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => <h1 className="mb-2 mt-3 text-xl font-semibold">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 mt-3 text-lg font-semibold">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1 mt-2 text-base font-semibold">{children}</h3>,
  p: ({ children }) => <p className="mb-2 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="mb-2 list-disc pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 list-decimal pl-5">{children}</ol>,
  li: ({ children }) => <li className="mb-0.5">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-4 border-slate-300 pl-3 text-slate-600">{children}</blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-blue-600 underline" target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <pre className="mb-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-slate-100">
          <code>{children}</code>
        </pre>
      );
    }
    return <code className="rounded bg-slate-100 px-1 py-0.5 text-[0.85em]">{children}</code>;
  },
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-left text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-slate-200 bg-slate-50 px-2 py-1">{children}</th>,
  td: ({ children }) => <td className="border border-slate-200 px-2 py-1">{children}</td>,
};

export type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
  id?: string;
  enableFileUpload?: boolean;
  className?: string;
};

type ToolbarAction = {
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

function getSelection(textarea: HTMLTextAreaElement): TextareaSelection {
  return {
    value: textarea.value,
    selectionStart: textarea.selectionStart,
    selectionEnd: textarea.selectionEnd,
  };
}

export default function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minHeight = 120,
  disabled = false,
  id,
  enableFileUpload = true,
  className,
}: MarkdownEditorProps) {
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingSelection = useRef<{ start: number; end: number } | null>(null);
  const historyRef = useRef<string[]>([]);
  const skipHistoryRef = useRef(false);

  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const [uploading, setUploading] = useState(false);
  const { triggerFileUpload } = useFileUpload('/file');

  useEffect(() => {
    if (pendingSelection.current && textareaRef.current) {
      const { start, end } = pendingSelection.current;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(start, end);
      pendingSelection.current = null;
    }
  }, [value]);

  const pushHistory = useCallback(
    (prev: string) => {
      if (skipHistoryRef.current) return;
      const stack = historyRef.current;
      if (stack.length === 0 || stack[stack.length - 1] !== prev) {
        stack.push(prev);
        if (stack.length > HISTORY_LIMIT) stack.shift();
      }
    },
    [],
  );

  const commitChange = useCallback(
    (next: string, selection?: { start: number; end: number }) => {
      pushHistory(value);
      if (selection) pendingSelection.current = selection;
      onChange(next);
    },
    [onChange, pushHistory, value],
  );

  const applyTransform = useCallback(
    (
      transform: (sel: TextareaSelection) => {
        nextValue: string;
        selectionStart: number;
        selectionEnd: number;
      },
    ) => {
      const el = textareaRef.current;
      if (!el || disabled) return;
      const result = transform(getSelection(el));
      commitChange(result.nextValue, {
        start: result.selectionStart,
        end: result.selectionEnd,
      });
    },
    [commitChange, disabled],
  );

  const handleUndo = useCallback(() => {
    const stack = historyRef.current;
    if (stack.length === 0 || disabled) return;
    const prev = stack.pop()!;
    skipHistoryRef.current = true;
    onChange(prev);
    skipHistoryRef.current = false;
  }, [disabled, onChange]);

  const uploadAndInsert = useCallback(
    async (file: File) => {
      if (!enableFileUpload || disabled) return;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await triggerFileUpload(formData);
        const raw = parseUploadedUrl(res);
        if (!raw) {
          toast({
            title: 'Xatolik',
            description: 'Fayl URL topilmadi',
            variant: 'destructive',
          });
          return;
        }
        const url = getMediaUrl(raw);
        const isImage = file.type.startsWith('image/');
        const md = markdownForFile(file.name, url, isImage);
        const el = textareaRef.current;
        if (el) {
          const sel = getSelection(el);
          const withNewline =
            sel.selectionStart > 0 && sel.value[sel.selectionStart - 1] !== '\n'
              ? `\n${md}\n`
              : `${md}\n`;
          const result = insertAtCursor(sel, withNewline);
          commitChange(result.nextValue, {
            start: result.selectionStart,
            end: result.selectionEnd,
          });
        } else {
          commitChange(value ? `${value}\n${md}` : md);
        }
        toast({ title: 'Muvaffaqiyatli', description: 'Fayl qo‘shildi' });
      } catch {
        toast({
          title: 'Xatolik',
          description: 'Fayl yuklanmadi',
          variant: 'destructive',
        });
      } finally {
        setUploading(false);
      }
    },
    [
      commitChange,
      disabled,
      enableFileUpload,
      toast,
      triggerFileUpload,
      value,
    ],
  );

  const handlePaste = (e: React.ClipboardEvent) => {
    const files = e.clipboardData?.files;
    if (files?.length && enableFileUpload) {
      e.preventDefault();
      void uploadAndInsert(files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && enableFileUpload) void uploadAndInsert(file);
  };

  const toolbarActions: ToolbarAction[] = [
    {
      title: 'Sarlavha',
      icon: <Heading className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => applyHeading(sel, 2)),
    },
    {
      title: 'Qalin',
      icon: <Bold className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => wrapSelection(sel, '**', '**', 'matn')),
    },
    {
      title: 'Kursiv',
      icon: <Italic className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => wrapSelection(sel, '*', '*', 'matn')),
    },
    {
      title: 'Iqtibos',
      icon: <Quote className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => prefixLines(sel, '> ')),
    },
    {
      title: 'Inline kod',
      icon: <Code className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => wrapSelection(sel, '`', '`', 'kod')),
    },
    {
      title: 'Kod bloki',
      icon: <span className="text-[10px] font-bold leading-none">&lt;&gt;</span>,
      onClick: () =>
        applyTransform((sel) => {
          const selected = sel.value.slice(sel.selectionStart, sel.selectionEnd);
          const body = selected || 'kod';
          return wrapSelection(sel, '```\n', '\n```', body);
        }),
    },
    {
      title: 'Havola',
      icon: <LinkIcon className="h-4 w-4" />,
      onClick: () =>
        applyTransform((sel) => wrapSelection(sel, '[', '](url)', 'matn')),
    },
    {
      title: 'Raqamli ro‘yxat',
      icon: <ListOrdered className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => prefixLines(sel, '1. ')),
    },
    {
      title: 'Ro‘yxat',
      icon: <List className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => prefixLines(sel, '- ')),
    },
    {
      title: 'Vazifalar ro‘yxati',
      icon: <ListChecks className="h-4 w-4" />,
      onClick: () => applyTransform((sel) => prefixLines(sel, '- [ ] ')),
    },
    {
      title: 'Fayl biriktirish',
      icon: uploading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Paperclip className="h-4 w-4" />
      ),
      onClick: () => fileInputRef.current?.click(),
      disabled: !enableFileUpload || uploading,
    },
    {
      title: 'Bekor qilish',
      icon: <Undo2 className="h-4 w-4" />,
      onClick: handleUndo,
    },
  ];

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-slate-200 bg-white text-sm shadow-sm',
        disabled && 'opacity-60 pointer-events-none',
        className,
      )}
    >
      <div className="flex items-center gap-0 border-b border-slate-200 bg-slate-50/80 px-2">
        <button
          type="button"
          className={cn(
            'border-b-2 px-3 py-2 text-xs font-medium transition-colors',
            mode === 'write'
              ? 'border-primary text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700',
          )}
          onClick={() => setMode('write')}
        >
          Write
        </button>
        <button
          type="button"
          className={cn(
            'border-b-2 px-3 py-2 text-xs font-medium transition-colors',
            mode === 'preview'
              ? 'border-primary text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700',
          )}
          onClick={() => setMode('preview')}
        >
          Preview
        </button>
      </div>

      {mode === 'write' && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 px-1 py-1">
          {toolbarActions.map((action) => (
            <button
              key={action.title}
              type="button"
              title={action.title}
              disabled={action.disabled}
              onClick={action.onClick}
              className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
            >
              {action.icon}
            </button>
          ))}
        </div>
      )}

      {mode === 'write' ? (
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={(e) => {
            pushHistory(value);
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          disabled={disabled}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="block w-full resize-y border-0 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-0"
          style={{ minHeight }}
        />
      ) : (
        <div className="px-3 py-2 text-sm text-slate-800" style={{ minHeight }}>
          {value.trim() ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={previewComponents}
            >
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-slate-400 italic">Preview uchun matn yo‘q</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/50 px-3 py-1.5 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="font-semibold text-slate-600">M↓</span>
          Markdown qo‘llab-quvvatlanadi
        </span>
        {enableFileUpload && mode === 'write' && (
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:text-slate-700"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <ImageIcon className="h-3 w-3" />
            Qo‘yish, tashlash yoki fayl qo‘shish
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void uploadAndInsert(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
